/**
 * SystemModeContext — Global context for test mode (JobLogic) state.
 * 
 * When test mode is enabled:
 * - All data operations use the test database
 * - Top nav shows blue banner with "JobLogic" branding
 * - Users can safely test without affecting live data
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getSystemMode, setSystemMode as apiSetSystemMode } from '../api/systemMode';
import { useAuth } from './AuthContext';

const SystemModeContext = createContext(null);

export function SystemModeProvider({ children }) {
  const { user } = useAuth();
  const [mode, setMode] = useState({
    test_mode_enabled: false,
    current_mode: 'live',
    display_name: 'Pump Source',
    loading: true,
  });

  const loadMode = useCallback(async () => {
    // Only fetch if user is authenticated
    if (!user) {
      setMode({
        test_mode_enabled: false,
        current_mode: 'live',
        display_name: 'Pump Source',
        loading: false,
      });
      return;
    }

    try {
      const data = await getSystemMode();
      setMode({
        ...data,
        loading: false,
      });
    } catch (err) {
      // If API fails, assume live mode
      setMode({
        test_mode_enabled: false,
        current_mode: 'live',
        display_name: 'Pump Source',
        loading: false,
      });
    }
  }, [user]);

  useEffect(() => {
    loadMode();
  }, [loadMode]);

  const toggleMode = useCallback(async () => {
    try {
      const data = await apiSetSystemMode('toggle');
      setMode({
        ...data,
        loading: false,
      });
      // Reload page to ensure all data is from correct database
      window.location.reload();
    } catch (err) {
      console.error('Failed to toggle system mode:', err);
      throw err;
    }
  }, []);

  const enableTestMode = useCallback(async () => {
    try {
      const data = await apiSetSystemMode('enable');
      setMode({
        ...data,
        loading: false,
      });
      window.location.reload();
    } catch (err) {
      console.error('Failed to enable test mode:', err);
      throw err;
    }
  }, []);

  const disableTestMode = useCallback(async () => {
    try {
      const data = await apiSetSystemMode('disable');
      setMode({
        ...data,
        loading: false,
      });
      window.location.reload();
    } catch (err) {
      console.error('Failed to disable test mode:', err);
      throw err;
    }
  }, []);

  const value = {
    ...mode,
    isTestMode: mode.test_mode_enabled,
    isLiveMode: !mode.test_mode_enabled,
    toggleMode,
    enableTestMode,
    disableTestMode,
    refresh: loadMode,
  };

  return (
    <SystemModeContext.Provider value={value}>
      {children}
    </SystemModeContext.Provider>
  );
}

export function useSystemMode() {
  const context = useContext(SystemModeContext);
  if (!context) {
    throw new Error('useSystemMode must be used within a SystemModeProvider');
  }
  return context;
}
